import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, assetUrl, errorText, useFetch } from "../api";
import { Alerts, Avatar, Badge, EmptyRow, Field, Loading, Page, Photo, SignedAmount, Tabs, UserLink } from "../components/ui";
import { label, money, percent, qs, text, when } from "../format";
import { WalletAdjust } from "./Members";

export default function MemberDetail() {
  const { id } = useParams();
  const { data, loading, error, reload } = useFetch("/members/" + id);
  const history = useFetch(id ? "/wallet-adjustments" + qs({ page: 1, limit: 10, userId: id }) : "");
  const detail = data?.data;
  const user = detail?.user;
  const [tab, setTab] = useState("purchases");
  const [adjust, setAdjust] = useState("");
  const [status, setStatus] = useState("active");
  const [restrictionReason, setRestrictionReason] = useState("");
  const [kycStatus, setKycStatus] = useState("pending");
  const [kycNote, setKycNote] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!user) return;
    setStatus(user.status || "active");
    setRestrictionReason(user.restrictionReason || "");
    setKycStatus(user.kycStatus || "pending");
    setKycNote(user.kycNote || "");
  }, [user]);

  async function save(kind, body, success) {
    setBusy(kind);
    setFormError("");
    setErrors({});
    setNotice("");
    try {
      await api("/members/" + id, { method: "PATCH", body });
      setNotice(success);
      reload();
    } catch (err) {
      setFormError(err.errors?.form || errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy("");
    }
  }

  function saveStatus(event) {
    event.preventDefault();
    const body = { status };
    if (status === "blocked") body.restrictionReason = restrictionReason.trim();
    save("status", body, "Member status saved");
  }

  function saveKyc(event) {
    event.preventDefault();
    save("kyc", { kycStatus, kycNote: kycNote.trim() }, "KYC saved");
  }

  return (
    <Page title="Member">
      <Link className="back" to="/admin/members">Back to members</Link>
      <Alerts error={error || formError} notice={notice} />
      {loading && !user ? <Loading /> : null}
      {user ? (
        <>
          <div className="panel">
            <div className="profile">
              <Avatar src={user.profileImage} name={user.name} />
              <div>
                <h2>{user.name}</h2>
                <p>{user.email}</p>
                <Badge value={user.status} />
              </div>
            </div>
            <div className="facts">
              <div><dt>Phone</dt><dd>{text(user.phone)}</dd></div>
              <div><dt>Country</dt><dd>{text(user.country)}</dd></div>
              <div><dt>Referral code</dt><dd className="code">{user.referralCode}</dd></div>
              <div className="wide">
                <dt>BEP20 deposit address</dt>
                <dd className="mono">{detail.bep20Address || "Not opened yet"}</dd>
              </div>
              <div><dt>Joined</dt><dd>{when(user.createdAt)}</dd></div>
              <div>
                <dt>Sponsor</dt>
                <dd>{detail.sponsor ? <UserLink user={detail.sponsor} /> : "No sponsor"}</dd>
              </div>
              <div><dt>Direct team</dt><dd>{detail.directTeam ?? 0}</dd></div>
              <div><dt>Active directs</dt><dd>{detail.activeDirects ?? 0}</dd></div>
              <div><dt>Unlocked levels</dt><dd>{detail.unlockedLevels ?? 0} / 5</dd></div>
              {user.restrictionReason ? (
                <div className="wide"><dt>Block reason</dt><dd>{user.restrictionReason}</dd></div>
              ) : null}
            </div>
          </div>

          <LevelTable rows={detail.levels} />

          <div className="balances">
            <div className="balance">
              <span>Wallet</span>
              <strong>{money(user.balance)}</strong>
              <div className="row-actions">
                <button type="button" className="btn small" onClick={() => setAdjust("credit")}>Credit</button>
                <button type="button" className="btn danger small" onClick={() => setAdjust("debit")}>Debit</button>
              </div>
            </div>
            <div className="balance"><span>Locked</span><strong>{money(user.lockedBalance)}</strong></div>
          </div>
          <AdjustmentRows rows={Array.isArray(history.data?.data) ? history.data.data : []} />
          {adjust ? (
            <WalletAdjust
              member={user}
              direction={adjust}
              onClose={() => setAdjust("")}
              onDone={(result) => {
                setAdjust("");
                setNotice(result.message || "Wallet updated");
                reload();
                history.reload();
              }}
            />
          ) : null}

          <div className="two">
            <form className="panel" onSubmit={saveStatus}>
              <h2 className="section-title">Account</h2>
              <Field label="Status" error={errors.status}>
                <select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="active">Active</option>
                  <option value="blocked">Blocked</option>
                </select>
              </Field>
              {status === "blocked" ? (
                <Field label="Block reason" error={errors.restrictionReason} hint="At least 3 characters. The member is notified.">
                  <textarea value={restrictionReason} onChange={(event) => setRestrictionReason(event.target.value)} />
                </Field>
              ) : <p className="hint">Active clears the block reason.</p>}
              <div className="form-actions">
                <button className="btn" disabled={busy === "status"}>{busy === "status" ? "Saving..." : "Save status"}</button>
              </div>
            </form>

            <form className="panel" onSubmit={saveKyc}>
              <h2 className="section-title">KYC</h2>
              <p className="hint">Document type: {text(user.kycDocumentType)}</p>
              {user.kycDocument ? (
                <a href={assetUrl(user.kycDocument)} target="_blank" rel="noreferrer">
                  <Photo src={user.kycDocument} className="kyc-doc" alt="KYC document" />
                </a>
              ) : <p className="hint">No document uploaded.</p>}
              <Field label="KYC status" error={errors.kycStatus}>
                <select value={kycStatus} onChange={(event) => setKycStatus(event.target.value)}>
                  <option value="pending">Pending</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </Field>
              <Field
                label="KYC note"
                error={errors.kycNote}
                hint={kycStatus === "rejected" ? "Required when rejected. The member is notified." : "The member is notified on approval or rejection."}
              >
                <textarea value={kycNote} onChange={(event) => setKycNote(event.target.value)} />
              </Field>
              <div className="form-actions">
                <button className="btn" disabled={busy === "kyc"}>{busy === "kyc" ? "Saving..." : "Save KYC"}</button>
              </div>
            </form>
          </div>

          <Tabs
            value={tab}
            onChange={setTab}
            options={[
              { value: "purchases", label: "Purchases" },
              { value: "deposits", label: "Deposits" },
              { value: "withdrawals", label: "Withdrawals" },
              { value: "statement", label: "Statement" },
            ]}
          />
          <p className="hint">Latest 20 from this member. Open a row for the full record.</p>
          {tab === "purchases" ? (
            <>
              <p><Link to={"/admin/purchases?userId=" + user.id}>Open all purchases</Link></p>
              <PurchaseRows rows={(detail.purchases || []).slice(0, 20)} />
            </>
          ) : null}
          {tab === "deposits" ? <DepositRows rows={(detail.deposits || []).slice(0, 20)} /> : null}
          {tab === "withdrawals" ? <WithdrawalRows rows={(detail.withdrawals || []).slice(0, 20)} /> : null}
          {tab === "statement" ? (
            <>
              <p><Link to={"/admin/earnings?userId=" + user.id}>Open earnings</Link></p>
              <StatementRows rows={(detail.transactions || []).slice(0, 20)} />
            </>
          ) : null}
        </>
      ) : null}
    </Page>
  );
}

function AdjustmentRows({ rows }) {
  return (
    <section className="panel level-panel">
      <h2 className="section-title">Wallet changes</h2>
      <p className="hint">Manual credits and debits on the spendable wallet. Locked balance is not included.</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Direction</th>
              <th className="num">Amount</th>
              <th className="num">Before</th>
              <th className="num">After</th>
              <th>Note</th>
              <th>Admin</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row) => (
              <tr key={row.id}>
                <td><Badge value={row.direction} /></td>
                <td className="num">{money(row.amount)}</td>
                <td className="num">{money(row.balanceBefore)}</td>
                <td className="num">{money(row.balanceAfter)}</td>
                <td>{text(row.note)}</td>
                <td>{text(row.admin?.name)}</td>
                <td>{when(row.createdAt)}</td>
              </tr>
            )) : <EmptyRow cols={7} text="No wallet changes" />}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LevelTable({ rows }) {
  const levels = Array.isArray(rows) ? rows : [];
  return (
    <section className="panel level-panel">
      <h2 className="section-title">Commission levels</h2>
      <p className="hint">Level 1 is this member as the direct sponsor. Level 5 is the fifth upline. A level unlocks when this member has that many direct referrals who bought a package. The percent is taken from the downline member's daily ROI and is paid each night. The one-time first-package payment is Direct commission % in Settings.</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Level</th>
              <th>Percent</th>
              <th>Buyers needed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {levels.length ? levels.map((row) => (
              <tr key={row.level}>
                <td>Level {row.level}</td>
                <td>{percent(row.percent)}</td>
                <td>{row.requiredDirects}</td>
                <td><Badge value={row.unlocked ? "unlocked" : "locked"} /></td>
              </tr>
            )) : <EmptyRow cols={4} text="No level settings" />}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PurchaseRows({ rows }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Package</th><th className="num">Paid</th><th>Rate</th><th>Days</th><th className="num">Earned</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row) => (
            <tr key={row.id}>
              <td><Link to={"/admin/purchases/" + row.id}>{row.packageName}</Link><div className="code">{row.packageCode}</div></td>
              <td className="num">{money(row.amount)}</td>
              <td>{percent(row.dailyRatePercent)}</td>
              <td>{row.creditedDays}/{row.durationDays}</td>
              <td className="num">{money(row.earnedAmount)}</td>
              <td><Badge value={row.status} /></td>
            </tr>
          )) : <EmptyRow cols={6} text="No purchases" />}
        </tbody>
      </table>
    </div>
  );
}

function DepositRows({ rows }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr><th className="num">Amount</th><th>Method</th><th>Reference</th><th>Status</th><th>Submitted</th></tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row) => (
            <tr key={row.id}>
              <td className="num"><Link to={"/admin/deposits/" + row.id}>{money(row.amount)}</Link></td>
              <td>{label(row.method)}</td>
              <td className="mono">{text(row.txHash)}</td>
              <td><Badge value={row.status} /></td>
              <td>{when(row.createdAt)}</td>
            </tr>
          )) : <EmptyRow cols={5} text="No deposits" />}
        </tbody>
      </table>
    </div>
  );
}

function WithdrawalRows({ rows }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr><th className="num">Requested</th><th className="num">Fee %</th><th className="num">Pay</th><th>Network</th><th>Address</th><th>Status</th></tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row) => (
            <tr key={row.id}>
              <td className="num">{money(row.amount)}</td>
              <td className="num">{percent(row.feePercent)}</td>
              <td className="num net"><Link to={"/admin/withdrawals/" + row.id}>{money(row.netAmount)}</Link></td>
              <td>{label(row.network)}</td>
              <td className="mono">{row.address}</td>
              <td><Badge value={row.status} /></td>
            </tr>
          )) : <EmptyRow cols={6} text="No withdrawals" />}
        </tbody>
      </table>
    </div>
  );
}

function StatementRows({ rows }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr><th>Type</th><th>Direction</th><th className="num">Amount</th><th>Note</th><th>Time</th></tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row) => (
            <tr key={row.id}>
              <td>{label(row.type)}</td>
              <td><Badge value={row.direction} /></td>
              <SignedAmount value={row.amount} direction={row.direction} />
              <td>{text(row.note)}</td>
              <td>{when(row.createdAt)}</td>
            </tr>
          )) : <EmptyRow cols={5} text="No statement rows" />}
        </tbody>
      </table>
    </div>
  );
}
